import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import StatCard from '../charts/StatCard';
import { DocumentTextIcon, UserPlusIcon, BeakerIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';

interface ClinicalIndicatorsProps {
  dateRange: { start_date?: string; end_date?: string };
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function ClinicalIndicators({ dateRange }: ClinicalIndicatorsProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const response = await api.get(`/dashboard/analytics/clinical/?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error loading clinical indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-500">Aucune donnée disponible</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Consultations"
          value={data.total_consultations}
          icon={<DocumentTextIcon className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Nouveaux Patients"
          value={data.new_patients?.count || 0}
          icon={<UserPlusIcon className="w-6 h-6" />}
          color="green"
        />
        <StatCard
          title="Types d'Actes"
          value={data.consultations_by_type?.length || 0}
          icon={<BeakerIcon className="w-6 h-6" />}
          color="purple"
        />
        <StatCard
          title="Consultations Non Facturées"
          value={data.billing_stats?.consultations_non_facturees || 0}
          icon={<ExclamationTriangleIcon className="w-6 h-6" />}
          color="yellow"
        />
      </div>

      {/* Billing Statistics - Only show if available */}
      {data.billing_stats && (
        <>
          {/* Billing Statistics Alert */}
          {data.billing_stats.consultations_non_facturees > 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Consultations non facturées détectées
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {data.billing_stats.consultations_non_facturees} consultation(s) sans facture, dont{' '}
                      {data.billing_stats.consultations_avec_traitements_non_facturees} avec traitements.
                    </p>
                    <p className="mt-1">
                      Montant potentiel non facturé: <span className="font-semibold">{data.billing_stats.montant_non_facture?.toLocaleString()} GNF</span>
                    </p>
                    <p className="mt-1">
                      Taux de facturation: <span className="font-semibold">{data.billing_stats.taux_facturation}%</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Rate Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Taux de Facturation</h3>
                <p className="text-sm text-gray-600">
                  Pourcentage de consultations facturées sur la période
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-gray-900">
                  {data.billing_stats?.taux_facturation || 0}%
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {(data.total_consultations - (data.billing_stats?.consultations_non_facturees || 0))} / {data.total_consultations} consultations
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    (data.billing_stats?.taux_facturation || 0) >= 80
                      ? 'bg-green-500'
                      : (data.billing_stats?.taux_facturation || 0) >= 50
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${data.billing_stats?.taux_facturation || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Consultations Evolution */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Consultations</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.consultations_evolution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}
            />
            <YAxis />
            <Tooltip
              labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Consultations"
              dot={{ fill: '#3b82f6' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consultations by Type */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultations par Type d'Acte</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data.consultations_by_type?.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="type"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" name="Nombre" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Prosthesis Delivered */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Prothèses Livrées par Type</h3>
          {data.prosthesis_delivered && data.prosthesis_delivered.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={data.prosthesis_delivered}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={({ type, count, percent }) =>
                    `${type}: ${count} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  {data.prosthesis_delivered.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-350 text-gray-500">
              Aucune prothèse livrée pour cette période
            </div>
          )}
        </div>
      </div>

      {/* Top Treatments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Actes par Revenus</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type d'Acte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenus Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.consultations_by_type?.slice(0, 10).map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.revenue?.toLocaleString()} GNF
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
